import React from 'react';
import i18n from 'i18n';
import { Trans } from 'react-i18next';
import classNames from 'classnames/bind';
import {
  WeatherVerificationSlotCount,
  WeatherVerificationSummary
} from 'core/report/WeatherVerification';
import { numberWithCommas } from 'utils/StringUtil';
import styles from './weatherVerificationSummaryCards.module.scss';

const cx = classNames.bind(styles);

const EMPTY_VALUE = '—';

/**
 * 門市時段的佔比，一位小數四捨五入（4/11 → 36.4%）。
 * 分母為 0 時沒有比例可言，顯示「—」而不是 0%，否則會被讀成「一個都沒有」。
 */
export const formatSlotPercent = (
  numerator: number,
  denominator: number
): string =>
  denominator === 0
    ? EMPTY_VALUE
    : `${(Math.round((numerator * 1000) / denominator) / 10).toFixed(1)}%`;

/** 副文字裡的數字依設計稿加粗 */
const BOLD = { b: <b /> };

type CardProps = {
  testId: string;
  /** 決定圓點與主值的顏色 */
  tone: string;
  title: string;
  value: React.ReactNode;
  lines: React.ReactNode[];
};

const SummaryCard: React.FC<CardProps> = ({
  testId,
  tone,
  title,
  value,
  lines
}) => (
  <div className={cx('card', tone)} data-testid={`summary-card-${testId}`}>
    <div className={styles.title}>
      <span className={styles.dot} />
      {title}
    </div>
    <div className={styles.value} data-testid={`summary-value-${testId}`}>
      {value}
    </div>
    {lines.map((line, index) => (
      <div key={index} className={styles.line}>
        {line}
      </div>
    ))}
  </div>
);

// 英文要依數量分單複數（1 store slot／2 store slots），所以 count 傳原始數字給 i18next 挑字，
// 畫面上顯示的千分位數字另外用 num 傳
const slotUnit = (slots: number) =>
  i18n.t('weatherVerification.summary.slotUnit', { count: slots });

const renderSlots = (slots: number) => (
  <>
    {numberWithCommas(slots)}{' '}
    <span className={styles.unit}>{slotUnit(slots)}</span>
  </>
);

const renderSlotLine = (slots: number) => (
  <>
    <b>{numberWithCommas(slots)}</b> {slotUnit(slots)}
  </>
);

const renderAffectedStores = ({ storeCount }: WeatherVerificationSlotCount) => (
  <Trans
    i18nKey='weatherVerification.summary.affectedStores'
    values={{ count: storeCount, num: numberWithCommas(storeCount) }}
    components={BOLD}
  />
);

type WeatherVerificationSummaryCardsProps = {
  summary?: WeatherVerificationSummary;
  loading: boolean;
  error: boolean;
};

/**
 * 機台投遞驗證的統整卡，單位是門市 × 小時（門市時段），與下方以機台 × 小時列出的明細不同。
 *
 * 分兩區：營業時間內的四類加起來就是檢驗時段（分母）；非營業時間不計入分母，
 * 單獨放在營業時間外，主值是時段數而不是百分比。
 */
export const WeatherVerificationSummaryCards: React.FC<
  WeatherVerificationSummaryCardsProps
> = ({ summary, loading, error }) => {
  if (error) {
    return (
      <div className={cx('area', 'message')}>
        {i18n.t('weatherVerification.summary.error')}
      </div>
    );
  }
  if (loading) {
    return (
      <div className={cx('area', 'message')}>
        {i18n.t('weatherVerification.summary.loading')}
      </div>
    );
  }
  if (!summary) {
    return null;
  }

  const {
    storeCount,
    dayCount,
    inspected,
    deliverable,
    trafficOccupied,
    weatherUnmatched,
    deviceAbnormal,
    outsideBusinessHours
  } = summary;
  const inspectedPercent = (count: WeatherVerificationSlotCount) =>
    formatSlotPercent(count.slots, inspected.slots);

  return (
    <div className={styles.area}>
      <div className={cx('section', 'inBusinessHours')}>
        <div className={styles.sectionTitle}>
          {i18n.t('weatherVerification.summary.sections.inBusinessHours')}
        </div>
        <div className={styles.cards}>
          <SummaryCard
            testId='inspected'
            tone='inspected'
            title={i18n.t('weatherVerification.summary.titles.inspected')}
            value={renderSlots(inspected.slots)}
            lines={[
              i18n.t('weatherVerification.summary.storesByDays', {
                stores: i18n.t('weatherVerification.summary.storeCount', {
                  count: storeCount,
                  num: numberWithCommas(storeCount)
                }),
                days: i18n.t('weatherVerification.summary.dayCount', {
                  count: dayCount,
                  num: dayCount
                })
              }),
              i18n.t('weatherVerification.summary.scheduleAndBusinessHours')
            ]}
          />
          <SummaryCard
            testId='deliverable'
            tone='deliverable'
            title={i18n.t('weatherVerification.filters.delivered')}
            value={inspectedPercent(deliverable)}
            lines={[
              renderSlotLine(deliverable.slots),
              <Trans
                key='openStores'
                i18nKey='weatherVerification.summary.openStores'
                values={{
                  // 單複數跟著「M 間中」的 M（0 of 1 store open）
                  count: storeCount,
                  num: numberWithCommas(storeCount),
                  open: numberWithCommas(deliverable.storeCount)
                }}
                components={BOLD}
              />
            ]}
          />
          <SummaryCard
            testId='weatherUnmatched'
            tone='weatherUnmatched'
            title={i18n.t('weatherVerification.filters.weatherUnmatched')}
            value={inspectedPercent(weatherUnmatched)}
            lines={[
              renderSlotLine(weatherUnmatched.slots),
              renderAffectedStores(weatherUnmatched)
            ]}
          />
          <SummaryCard
            testId='deviceAbnormal'
            tone='deviceAbnormal'
            title={i18n.t('weatherVerification.filters.deviceAbnormal')}
            value={inspectedPercent(deviceAbnormal)}
            lines={[
              renderSlotLine(deviceAbnormal.slots),
              renderAffectedStores(deviceAbnormal)
            ]}
          />
          <SummaryCard
            testId='trafficOccupied'
            tone='trafficOccupied'
            title={i18n.t('weatherVerification.filters.trafficOccupied')}
            value={inspectedPercent(trafficOccupied)}
            lines={[
              renderSlotLine(trafficOccupied.slots),
              renderAffectedStores(trafficOccupied)
            ]}
          />
        </div>
      </div>
      <div className={cx('section', 'outsideBusinessHours')}>
        <div className={styles.sectionTitle}>
          {i18n.t('weatherVerification.summary.sections.outsideBusinessHours')}
        </div>
        <div className={styles.cards}>
          <SummaryCard
            testId='outsideBusinessHours'
            tone='outsideBusinessHours'
            title={i18n.t('weatherVerification.filters.outsideBusinessHours')}
            value={renderSlots(outsideBusinessHours.slots)}
            lines={[
              i18n.t('weatherVerification.summary.outsideHint'),
              <Trans
                key='outsideShare'
                i18nKey='weatherVerification.summary.outsideShare'
                values={{
                  // 佔的是活動有排的全部時段，所以分母要把非營業時間加回來
                  percent: formatSlotPercent(
                    outsideBusinessHours.slots,
                    inspected.slots + outsideBusinessHours.slots
                  )
                }}
                components={BOLD}
              />
            ]}
          />
        </div>
      </div>
    </div>
  );
};
